/**
 * ResourceGrid — flat grid of all resource detail cards.
 *
 * Renders Ec2Card, RdsCard, and EcsCard instances in a CSS grid layout.
 * Stacks come from useStacks; this component flattens them for the flat view.
 */
import type { ApplicationStack } from '@/types';
import { Ec2Card } from './cards/Ec2Card';
import { RdsCard } from './cards/RdsCard';
import { EcsCard } from './cards/EcsCard';
import styles from './ResourceGrid.module.css';

export interface ResourceGridProps {
  stacks: ApplicationStack[];
  isLoading: boolean;
}

export function ResourceGrid({ stacks, isLoading }: ResourceGridProps) {
  // Flatten all resources from all stacks
  const allEc2 = stacks.flatMap((s) => s.ec2Instances);
  const allRds = stacks.flatMap((s) => s.rdsInstances);
  const allEcs = stacks.flatMap((s) => s.ecsClusters);
  const total = allEc2.length + allRds.length + allEcs.length;

  if (isLoading) {
    return (
      <div className={styles.grid} aria-busy="true" aria-label="Loading resources">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={styles.skeleton} aria-hidden="true" />
        ))}
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className={styles.empty} role="status">
        <p>No resources match the current filters.</p>
      </div>
    );
  }

  return (
    <div className={styles.grid}>
      {allEc2.map((instance) => (
        <Ec2Card key={instance.resourceId} instance={instance} />
      ))}
      {allRds.map((instance) => (
        <RdsCard key={instance.resourceId} instance={instance} />
      ))}
      {allEcs.map((cluster) => (
        <EcsCard key={cluster.resourceId} cluster={cluster} />
      ))}
    </div>
  );
}
